import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans as TransBase } from 'react-i18next';
import PublicHeader from '../components/PublicHeader';
import { useBilling } from '../billing';
import { useAuth } from '../auth';
import { capture } from '../analytics';

// react-i18next v17 Trans types use TS 5 generics incompatible with TS 4.9
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Trans = TransBase as any;

const PLAN_LABELS: Record<string, string> = { starter: 'Starter', pro: 'Pro' };

type Currency = 'USD' | 'GBP' | 'EUR' | 'BRL';

const PRICE_DISPLAY: Record<Currency, Record<string, { monthly: string; annual: string; annualBilled: string }>> = {
  USD: {
    starter: { monthly: '$25/mo',    annual: '$18.75/mo', annualBilled: 'billed $225/yr' },
    pro:     { monthly: '$45/mo',    annual: '$33.75/mo', annualBilled: 'billed $405/yr' },
  },
  GBP: {
    starter: { monthly: '£19.99/mo', annual: '£14.99/mo', annualBilled: 'billed £179.88/yr' },
    pro:     { monthly: '£35.99/mo', annual: '£26.99/mo', annualBilled: 'billed £323.88/yr' },
  },
  EUR: {
    starter: { monthly: '€23.99/mo', annual: '€17.99/mo', annualBilled: 'billed €215.88/yr' },
    pro:     { monthly: '€42.99/mo', annual: '€31.99/mo', annualBilled: 'billed €383.88/yr' },
  },
  BRL: {
    starter: { monthly: 'R$99.90/mo',  annual: 'R$74.90/mo',  annualBilled: 'billed R$898.80/yr' },
    pro:     { monthly: 'R$179.90/mo', annual: 'R$134.90/mo', annualBilled: 'billed R$1,618.80/yr' },
  },
};

export default function PricingCheckoutPage() {
  const { t } = useTranslation();
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const { billing } = useBilling();

  const planParam     = params.get('plan') ?? billing.plan ?? 'starter';
  const cycleParam    = params.get('cycle') ?? billing.billingCycle ?? 'annual';
  const currencyParam = (params.get('currency') ?? localStorage.getItem('teh_preferred_currency') ?? 'USD') as Currency;

  const [plan,         setPlan]         = useState(planParam);
  const [billingCycle, setBillingCycle] = useState(cycleParam);
  const [currency]                      = useState<Currency>(
    currencyParam in PRICE_DISPLAY ? currencyParam : 'USD'
  );
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  useEffect(() => {
    capture('trial_checkout_viewed', { plan, billing_cycle: billingCycle });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already on a paid plan — send to dashboard
  if (billing.status === 'active') {
    navigate('/app/dashboard', { replace: true });
    return null;
  }

  // Conversion mode: user has a trial and is adding payment details
  const isConversion = billing.status === 'trialing';

  const currencyPrices = PRICE_DISPLAY[currency] ?? PRICE_DISPLAY.USD;
  const priceInfo = currencyPrices[plan] ?? currencyPrices.starter;
  const displayPrice = billingCycle === 'annual'
    ? `${priceInfo.annual} (${priceInfo.annualBilled})`
    : priceInfo.monthly;

  function redirectToAuth() {
    window.location.href = `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(
      `/pricing/checkout?plan=${plan}&cycle=${billingCycle}`
    )}`;
  }

  async function startTrial() {
    if (!user) { redirectToAuth(); return; }

    capture('trial_checkout_started', { plan, billing_cycle: billingCycle, provider: 'none' });
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/billing/start-trial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, billingCycle }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      navigate('/app/dashboard?billing=trial-started');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  async function startCheckout(provider: 'stripe' | 'gocardless') {
    if (!user) { redirectToAuth(); return; }

    capture('trial_checkout_started', { plan, billing_cycle: billingCycle, provider });
    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/billing/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, billingCycle, provider, currency }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      const { url } = await res.json();
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  return (
    <div className="landing">
      <PublicHeader />

      <main className="checkout-page">
        <div className="checkout-card">
          <Link to="/pricing" className="checkout-back">{t('checkout.backToPricing')}</Link>

          <h1 className="checkout-title">
            {isConversion ? t('checkout.upgradeTitle', { plan: PLAN_LABELS[plan] }) : t('checkout.trialTitle')}
          </h1>
          <p className="checkout-subtitle">
            {isConversion
              ? t('checkout.upgradeSubtitle', { price: displayPrice })
              : t('checkout.trialSubtitle')}
          </p>

          {/* Plan selector */}
          <div className="checkout-section-label">{t('checkout.plan')}</div>
          <div className="checkout-toggle">
            {(['starter', 'pro'] as const).map(p => (
              <button
                key={p}
                className={`checkout-toggle-btn${plan === p ? ' checkout-toggle-btn--active' : ''}`}
                onClick={() => setPlan(p)}
              >
                {PLAN_LABELS[p]}
              </button>
            ))}
          </div>

          {/* Billing cycle selector */}
          <div className="checkout-section-label">{t('checkout.billing')}</div>
          <div className="checkout-toggle">
            <button
              className={`checkout-toggle-btn${billingCycle === 'monthly' ? ' checkout-toggle-btn--active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              {t('pricing.monthly')}
            </button>
            <button
              className={`checkout-toggle-btn${billingCycle === 'annual' ? ' checkout-toggle-btn--active' : ''}`}
              onClick={() => setBillingCycle('annual')}
            >
              {t('pricing.annual')} <span className="checkout-save-badge">−25%</span>
            </button>
          </div>

          {/* Price summary */}
          <div className="checkout-price-summary">
            <span className="checkout-price-plan">{PLAN_LABELS[plan]}</span>
            <span className="checkout-price-amount">{displayPrice}</span>
          </div>

          {error && <p className="checkout-error">{error}</p>}

          {isConversion ? (
            <>
              {/* Conversion: show payment method buttons */}
              <div className="checkout-section-label">{t('checkout.paymentMethod')}</div>
              <div className="checkout-payment-options">
                <button
                  className="checkout-pay-btn checkout-pay-btn--primary"
                  onClick={() => startCheckout('stripe')}
                  disabled={loading}
                >
                  {loading ? t('checkout.redirecting') : `💳  ${t('checkout.payByCard')}`}
                </button>
                {currency === 'GBP' && (
                  <button
                    className="checkout-pay-btn checkout-pay-btn--secondary"
                    onClick={() => startCheckout('gocardless')}
                    disabled={loading}
                  >
                    {`🏦  ${t('checkout.payByDirectDebit')}`}
                  </button>
                )}
              </div>
              <p className="checkout-legal">
                <Trans i18nKey="checkout.legal" components={{
                  termsLink: <Link to="/terms" />,
                  privacyLink: <Link to="/privacy" />,
                }} />
              </p>
            </>
          ) : (
            <>
              {/* Trial start: single CTA, no card required */}
              <button
                className="checkout-pay-btn checkout-pay-btn--primary"
                onClick={startTrial}
                disabled={loading}
                style={{ marginTop: '1rem' }}
              >
                {loading ? t('checkout.starting') : t('checkout.startTrialCta')}
              </button>
              <p className="checkout-legal">
                <Trans i18nKey="checkout.legalTrial" components={{
                  termsLink: <Link to="/terms" />,
                  privacyLink: <Link to="/privacy" />,
                }} />
              </p>
            </>
          )}
        </div>
      </main>

      <footer className="landing-footer">
        {t('common.copyright', { year: new Date().getFullYear() })} ·{' '}
        <Link to="/privacy">{t('landing.footer.privacy')}</Link>
      </footer>
    </div>
  );
}
